<script setup lang="ts">
    import { computed } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { useQueryGetBackfillAnalysisReceivedAtJobs } from '@/js/services/api/backOffice/backOfficeQueries';
    import { SyncJobStatus } from '@/js/services/api/backOffice/types';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import { RefreshCwIcon } from '@lucide/vue';

    const {
        data: jobs,
        isLoading: isLoadingJobs,
        isFetching: isFetchingJobs,
        refetch: refetchJobs,
    } = useQueryGetBackfillAnalysisReceivedAtJobs();

    defineExpose({ refetch: refetchJobs });

    const statusDisplay = computed(() => ({
        [SyncJobStatus.Waiting]: {
            label: __('back-office-job-table-status-queued'),
            color: 'text-gray-medium',
        },
        [SyncJobStatus.Running]: {
            label: __('back-office-job-table-status-running'),
            color: 'text-blue-500',
        },
        [SyncJobStatus.Success]: {
            label: __('back-office-job-table-status-succeeded'),
            color: 'text-success',
        },
        [SyncJobStatus.Failed]: {
            label: __('back-office-job-table-status-failed'),
            color: 'text-error',
        },
    }));

    function formatDate(date: string | null): string {
        return date ? new Date(date).toLocaleString() : '-';
    }
</script>
<template>
    <div>
        <div class="flex items-center gap-2 mb-2">
            <h2 class="heading-md">
                {{ __('back-office-backfill-analysis-received-at-jobs-heading') }}
            </h2>
            <EchoTooltip :label="__('back-office-job-table-refresh')">
                <RefreshCwIcon
                    :class="[
                        'w-5 h-5 cursor-pointer hover:text-gray-medium transition-colors',
                        { 'animate-spin': isFetchingJobs },
                    ]"
                    @click="refetchJobs()"
                />
            </EchoTooltip>
            <p class="text-caption text-gray-medium">
                {{ __('back-office-job-table-count', { count: String(jobs?.length ?? 0) }) }}
            </p>
        </div>
        <div class="overflow-x-auto">
            <table class="w-full border-collapse">
                <thead>
                    <tr class="border-b border-gray-lightest">
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-status') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-backfill-analysis-received-at-jobs-col-tenant') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-backfill-analysis-received-at-jobs-col-result') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-started') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-ended') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="isLoadingJobs">
                        <td colspan="5" class="py-12 text-center"><LoadingDots /></td>
                    </tr>
                    <template v-else>
                        <tr v-if="!jobs?.length">
                            <td colspan="5" class="py-6 text-body text-gray-medium text-center">
                                {{ __('back-office-job-table-empty') }}
                            </td>
                        </tr>
                        <tr v-for="job in jobs" :key="job.id" class="border-b border-gray-lightest">
                            <td
                                class="py-3 pr-6 text-left text-body"
                                :class="statusDisplay[job.status].color"
                            >
                                <EchoTooltip
                                    v-if="job.status === SyncJobStatus.Failed && job.output"
                                    :label="job.output.split(/\r?\n/)[0]"
                                >
                                    <span class="underline decoration-dotted">
                                        {{ statusDisplay[job.status].label }}
                                    </span>
                                </EchoTooltip>
                                <template v-else>{{ statusDisplay[job.status].label }}</template>
                            </td>
                            <td class="py-3 pr-6 text-left text-body">{{ job.tenantName }}</td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ job.status === SyncJobStatus.Success ? job.output : '-' }}
                            </td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ formatDate(job.startedAt) }}
                            </td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ formatDate(job.endedAt) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>
